import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { RewardService } from '../services/rewardService';
import { CardService } from '../services/cardService';
import { useAuth } from '../context/AuthContext';
import type { RewardV1SearchResult } from '../types/rewardSearchV1';
import type { Card } from '../types/card';

const rpValueInInr = 0.25;

const defaultSearchAmount = 5000;
const defaultSearchFrequency = 'monthly';

const getMonthlyAmount = (amount: number, frequency?: string) => {
  switch (frequency) {
    case 'quarterly':
      return amount / 3;
    case 'half_yearly':
      return amount / 6;
    case 'annually':
      return amount / 12;
    default:
      return amount;
  }
};

const calculateRewardPoints = (amount: number, frequency: string | undefined, effectiveRatePerInr: number, monthlyLimit?: number) => {
  const monthlyAmount = getMonthlyAmount(amount, frequency);
  let rewardPoints = monthlyAmount * effectiveRatePerInr;
  if (typeof monthlyLimit === 'number' && monthlyLimit > 0 && rewardPoints > monthlyLimit) {
    rewardPoints = monthlyLimit;
  }
  return Math.round(rewardPoints);
};

const calculateRewardPercentage = (amount: number, frequency: string | undefined, rewardPoints: number) => {
  const monthlyAmount = getMonthlyAmount(amount, frequency);
  if (!monthlyAmount || monthlyAmount <= 0) return 0;
  const rewardValue = rewardPoints * rpValueInInr;
  return Math.round((rewardValue / monthlyAmount) * 100 * 10) / 10;
};

const accelerationRatio = (acceleratedValue?: number, baseValue?: number) => {
  if (typeof acceleratedValue === 'number' && typeof baseValue === 'number' && baseValue > 0) {
    return Math.round(acceleratedValue / baseValue);
  }
  return null;
};

const ExpenseRewardSearch: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = (searchParams.get('q') || '').trim();
  const question = (searchParams.get('question') || '').trim();
  const amountParam = searchParams.get('amount');
  const frequencyParam = (searchParams.get('frequency') || '').trim();
  const cardIdParam = (searchParams.get('cardid') || '').trim();
  const legacyCardLabelParam = (searchParams.get('card') || '').trim();

  const getInitialAmountInput = () => {
    const n = amountParam ? Number(amountParam) : NaN;
    return Number.isFinite(n) && n > 0 ? String(n) : String(defaultSearchAmount);
  };

  const getInitialFrequency = () => {
    return frequencyParam || defaultSearchFrequency;
  };

  const [amountInput, setAmountInput] = useState<string>(() => getInitialAmountInput());
  const [frequency, setFrequency] = useState<string>(() => getInitialFrequency());

  useEffect(() => {
    const nextAmount = getInitialAmountInput();
    const nextFrequency = getInitialFrequency();
    setAmountInput((prev) => (prev === nextAmount ? prev : nextAmount));
    setFrequency((prev) => (prev === nextFrequency ? prev : nextFrequency));
  }, [amountParam, frequencyParam]);

  const amount = (() => {
    const n = Number(amountInput);
    return Number.isFinite(n) && n > 0 ? n : defaultSearchAmount;
  })();

  const isLoggedIn = Boolean(user);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<RewardV1SearchResult[]>([]);

  const [cardInput, setCardInput] = useState<string>(() => legacyCardLabelParam);
  const [cardDropdownOpen, setCardDropdownOpen] = useState<boolean>(false);
  const [cardLoading, setCardLoading] = useState<boolean>(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardOptions, setCardOptions] = useState<Card[]>([]);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  useEffect(() => {
    if (!cardIdParam) {
      setSelectedCard(null);
      if (legacyCardLabelParam) {
        setCardInput((prev) => (prev === legacyCardLabelParam ? prev : legacyCardLabelParam));
      }
    }
  }, [cardIdParam, legacyCardLabelParam]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (!cardIdParam) return;

    let isMounted = true;
    (async () => {
      try {
        const res = await CardService.getCardById(cardIdParam);
        if (!isMounted) return;
        setSelectedCard(res.data);
        setCardInput(`${res.data.bankName} — ${res.data.name}`);
      } catch {
        if (!isMounted) return;
        setSelectedCard(null);
        setCardInput(cardIdParam);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [cardIdParam, isLoggedIn]);

  useEffect(() => {
    document.title = 'Reward Search';
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !cardDropdownOpen) return;

    let isMounted = true;
    const handle = setTimeout(async () => {
      try {
        setCardLoading(true);
        setCardError(null);

        const search = cardInput.trim();
        const res = await CardService.getCards({
          page: 1,
          limit: 20,
          ...(search ? { search } : {}),
        });

        if (!isMounted) return;
        setCardOptions(res.data || []);
      } catch (e) {
        if (!isMounted) return;
        setCardError(e instanceof Error ? e.message : 'Failed to load cards');
        setCardOptions([]);
      } finally {
        if (!isMounted) return;
        setCardLoading(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(handle);
    };
  }, [cardInput, cardDropdownOpen, isLoggedIn]);

  useEffect(() => {
    if (!q) {
      setResults([]);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const cardIdForSearch = selectedCard?._id || cardIdParam || undefined;
        const res = await RewardService.searchRewardsV1(q, { userId, cardId: cardIdForSearch });
        if (!isMounted) return;
        setResults(res.data || []);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to search rewards');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [q, userId, cardIdParam, selectedCard?._id]);

  const sortedTop = useMemo(() => {
    const list = [...(results || [])];
    list.sort((a, b) => {
      const aInactive = String(a.status || '').toLowerCase() === 'inactive';
      const bInactive = String(b.status || '').toLowerCase() === 'inactive';
      if (aInactive !== bInactive) return aInactive ? 1 : -1;
      const ar = a.rewards?.dynamic?.effective_rate_per_inr ?? 0;
      const br = b.rewards?.dynamic?.effective_rate_per_inr ?? 0;
      return br - ar;
    });
    return list.slice(0, 4);
  }, [results]);

  const visibleOptions = useMemo(() => {
    if (isLoggedIn) return sortedTop;
    return sortedTop.slice(0, 2);
  }, [isLoggedIn, sortedTop]);

  const amountLabel = frequency === 'one-time' ? 'One-time' : 'Monthly';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reward Options</h1>
          <p className="mt-1 text-sm text-gray-500">
            {question ? `Question: ${question}` : null}
            {question ? ' • ' : null}
            Search: {q || '—'} {userId ? `• User: ${userId}` : ''}
            {' • '}
            Amount: ₹{amount.toLocaleString('en-IN')} • Frequency: {frequency || defaultSearchFrequency}
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      {!q && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-700">No query provided.</div>
        </div>
      )}

      {!userId && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="relative flex-1 min-w-[240px]">
            <label className="block text-xs font-medium text-gray-700">Credit card (search & select)</label>
            <input
              value={cardInput}
              onChange={(e) => {
                setSelectedCard(null);
                setCardInput(e.target.value);
                setCardDropdownOpen(true);
              }}
              onFocus={() => setCardDropdownOpen(true)}
              onBlur={() => {
                // allow click selection from dropdown
                setTimeout(() => setCardDropdownOpen(false), 150);
              }}
              disabled={!isLoggedIn}
              placeholder="Type bank or card name (e.g., hdfc)"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
            />

            {!isLoggedIn && (
              <div className="mt-2 text-xs text-gray-500">Login to search and select your saved cards.</div>
            )}

            {cardDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
                {cardLoading && (
                  <div className="px-3 py-2 text-sm text-gray-500">Loading…</div>
                )}

                {cardError && !cardLoading && (
                  <div className="px-3 py-2 text-sm text-red-700">{cardError}</div>
                )}

                {!cardLoading && !cardError && cardOptions.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">No cards found.</div>
                )}

                {!cardLoading && !cardError && cardOptions.length > 0 && (
                  <div className="max-h-64 overflow-auto">
                    {cardOptions.map((c) => (
                      <button
                        key={c._id}
                        type="button"
                        onMouseDown={(e) => {
                          // prevent input blur from closing before click
                          e.preventDefault();
                        }}
                        onClick={() => {
                          setSelectedCard(c);
                          setCardInput(`${c.bankName} — ${c.name}`);
                          setCardDropdownOpen(false);

                          // Preserve selection across navigation (e.g. option detail -> back)
                          // by writing the selected card id into the URL.
                          const params = new URLSearchParams(searchParams);
                          params.set('cardid', c._id);
                          // remove legacy param if present
                          params.delete('card');
                          setSearchParams(params);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedCard?._id === c._id ? 'bg-gray-50' : ''}`}
                      >
                        <div className="text-gray-900">{c.bankName} — {c.name}</div>
                        <div className="text-xs text-gray-500 break-all">{c._id}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedCard && !userId && (
              <div className="mt-2 text-xs text-gray-500">
                Selected: {selectedCard.bankName} — {selectedCard.name}
              </div>
            )}
          </div>

          <div className="min-w-[140px]">
            <label className="block text-xs font-medium text-gray-700">Amount</label>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="min-w-[180px]">
            <label className="block text-xs font-medium text-gray-700">Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="half_yearly">Half Yearly</option>
              <option value="annually">Annually</option>
              <option value="one-time">One-time</option>
            </select>
          </div>

          <div>
            <button
              type="button"
              onClick={() => {
                const params = new URLSearchParams(searchParams);

                params.set('amount', String(amount));
                params.set('frequency', (frequency || '').trim() || defaultSearchFrequency);

                if (selectedCard?._id) params.set('cardid', selectedCard._id);
                else params.delete('cardid');
                // remove legacy param if present
                params.delete('card');

                setCardDropdownOpen(false);
                setSearchParams(params);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Search
            </button>
          </div>
        </div>
      </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>}

      {loading && (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <p className="mt-2 text-gray-500">Searching rewards...</p>
        </div>
      )}

      {!loading && q && (
        <div className="space-y-4">
          {visibleOptions.map((opt, idx) => {
            const isInactive = String(opt.status || '').toLowerCase() === 'inactive';
            const effective = opt.rewards?.dynamic?.effective_rate_per_inr ?? 0;
            const monthlyLimit = opt.rewards?.limit;

            const canCalc = typeof amount === 'number' && Number.isFinite(amount) && amount > 0;
            const rewardPoints = canCalc ? calculateRewardPoints(amount, frequency, effective, monthlyLimit) : null;
            const pct = canCalc && rewardPoints !== null ? calculateRewardPercentage(amount, frequency, rewardPoints) : null;
            const accel = accelerationRatio(opt.rewards?.dynamic?.accelerated_value, opt.rewards?.dynamic?.base_value);

            const isPreview = !isLoggedIn && idx === 0;
            const isBlurred = !isLoggedIn && idx === 1;

            const onClick = () => {
              if (!isLoggedIn) {
                navigate('/login');
                return;
              }
              navigate('/rewardsv1/option-detail', {
                state: {
                  rewardOption: opt,
                  currentAmount: amount,
                  currentFrequency: frequency,
                  from: location.pathname + location.search,
                },
              });
            };

            const onEdit = () => {
              if (!isLoggedIn) {
                navigate('/login');
                return;
              }
              window.open(`/rewards/edit/${opt._id}`, '_blank', 'noopener,noreferrer');
            };

            const onDuplicate = () => {
              if (!isLoggedIn) {
                navigate('/login');
                return;
              }
              window.open(`/rewards/new/${opt._id}`, '_blank', 'noopener,noreferrer');
            };

            return (
              <div
                key={opt._id}
                className={`${
                  isInactive
                    ? 'bg-red-50 border-red-200'
                    : 'bg-white border-gray-200'
                } rounded-lg shadow-sm border p-5 ${isBlurred ? 'relative' : ''}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-semibold">
                        {idx + 1}
                      </span>
                      {isPreview && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          FREE PREVIEW
                        </span>
                      )}
                      {isInactive && !isBlurred && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          INACTIVE
                        </span>
                      )}
                    </div>
                    <div className={`mt-2 text-lg font-semibold text-gray-900 ${isBlurred ? 'blur-sm select-none' : ''}`}>{opt.name}</div>
                    <div className={`mt-1 text-sm text-gray-600 ${isBlurred ? 'blur-sm select-none' : ''}`}>
                      {(opt.bank_name || '—')} — {(opt.card_name || '—')}
                    </div>

                    <div className={`mt-3 text-sm text-gray-800 ${isBlurred ? 'blur-sm select-none' : ''}`}>
                      Reward rate: {opt.rewards?.description || '—'}
                    </div>
                    {typeof opt.rewards?.limit === 'number' && (
                      <div className={`mt-1 text-sm text-gray-700 ${isBlurred ? 'blur-sm select-none' : ''}`}>
                        Monthly cap: {opt.rewards.limit.toLocaleString('en-IN')} RPs
                      </div>
                    )}
                    {opt.categoryprimary && (
                      <div className={`mt-1 text-sm text-gray-700 ${isBlurred ? 'blur-sm select-none' : ''}`}>
                        Category: {opt.categoryprimary}
                      </div>
                    )}
                  </div>

                  <div className="text-right">
                    {canCalc && rewardPoints !== null && (
                      <>
                        <div className={`text-sm text-gray-700 ${isBlurred ? 'blur-sm select-none' : ''}`}>
                          You Earn Approx ({amountLabel}): <span className="font-semibold">{rewardPoints.toLocaleString('en-IN')}</span> RPs
                        </div>
                        {pct !== null && (
                          <div className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ${isBlurred ? 'blur-sm select-none' : ''}`}>
                            {pct}% rewards
                          </div>
                        )}
                        {accel !== null && (
                          <div className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ${isBlurred ? 'blur-sm select-none' : ''}`}>
                            {accel}X Returns
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {isBlurred ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white/90 border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800">
                      Login to view more options.
                    </div>
                  </div>
                ) : (
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={onClick}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        View Details
                      </button>
                      <button
                        onClick={onEdit}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={onDuplicate}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Duplicate
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {!loading && q && visibleOptions.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-700">No results found.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpenseRewardSearch;
